import { MessageBarType } from '@fluentui/react';
import { useToast } from '@h2oai/ui-kit';
import React, { useMemo } from 'react';

import { useInfiniteQueryFetchAllPages } from '../../services/hooks';
import { useUser } from '../../utils/hooks';
import { arrayToObject, formatError } from '../../utils/utils';
import { useQueryGroups, useQueryUsers } from '../authz.hooks';
import { useAuthzService } from '../authz.hooks';
import { Group } from '../gen/ai/h2o/user/v1/group_pb';
import { User } from '../gen/ai/h2o/user/v1/user_pb';

type UsersAndGroupsResponse = {
  users: User[];
  groups: Group[];
};

type UsersContextType = {
  listUsers: (searchFilter: string) => Promise<User[] | undefined>;
  listGroups: (searchFilter: string) => Promise<Group[] | undefined>;
  listGroupsAndUsers: (searchFilter: string) => Promise<UsersAndGroupsResponse | undefined>;
  loading: boolean;
  users?: User[];
  groups?: Group[];
  usersAndGroups?: (User | Group)[];
  CURRENT_USER_NAME?: string;
  usersNameMap?: { [key: string]: User };
  groupsNameMap?: { [key: string]: Group };
  usersAndGroupsNameMap?: { [key: string]: Group | User };
};

const UsersContext = React.createContext<UsersContextType | undefined>(undefined);

const UsersProvider = ({ children }: { children: React.ReactNode }) => {
  const authzService = useAuthzService(),
    { addToast } = useToast(),
    user = useUser(),
    CURRENT_USER_NAME = `users/${user.id}`;

  const usersQuery = useQueryUsers(
    {},
    {
      enabled: !user.isPublic,
      meta: {
        toastError: (err: unknown) => {
          const message = `Failed to fetch users: ${formatError(err)}`;
          console.error(message);
          return message;
        },
      },
      select: (data) => {
        const flattenedUsers = data.pages.flatMap((page) => {
          return page.users || [];
        });
        return { pages: flattenedUsers, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(usersQuery);
  const users = usersQuery.data?.pages;
  const usersNameMap = useMemo(() => arrayToObject(users || [], 'name'), [users]);

  const groupsQuery = useQueryGroups(
    {},
    {
      enabled: !user.isPublic,
      meta: {
        toastError: (err: unknown) => {
          const message = `Failed to fetch groups: ${formatError(err)}`;
          console.error(message);
          return message;
        },
      },
      select: (data) => {
        const flattenedGroups = data.pages.flatMap((page) => {
          return page.groups || [];
        });
        return { pages: flattenedGroups, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(groupsQuery);
  const groups = groupsQuery.data?.pages;
  const groupsNameMap = useMemo(() => arrayToObject(groups || [], 'name'), [groups]);

  const usersAndGroupsNameMap = useMemo(() => ({ ...usersNameMap, ...groupsNameMap }), [usersNameMap, groupsNameMap]);

  const listUsers = async (searchFilter: string) => {
      try {
        const data = await authzService.getUsers({ filter: `'${searchFilter}'` });
        const userItems: User[] | undefined = data?.users;
        if (data && !data?.users) console.error('No users found in the response.');
        return userItems;
      } catch (err) {
        const message = `Failed to fetch users: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return undefined;
      }
    },
    listGroups = async (searchFilter: string) => {
      try {
        const data = await authzService.getGroups({ filter: `'${searchFilter}'` });
        const groupItems: User[] | undefined = data?.groups;
        if (data && !data?.groups) console.error('No groups found in the response.');
        return groupItems;
      } catch (err) {
        const message = `Failed to fetch groups: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return undefined;
      }
    },
    listGroupsAndUsers = async (searchFilter: string) => {
      const requestPayload = { filter: `"${searchFilter}"` };
      try {
        const [usersResponse, groupsResponse] = await Promise.allSettled([
          authzService.getUsers(requestPayload),
          authzService.getGroups(requestPayload),
        ]);

        if (usersResponse.status === 'rejected') console.error('Failed to fetch users:', usersResponse.reason);
        if (groupsResponse.status === 'rejected') console.error('Failed to fetch groups:', groupsResponse.reason);

        const users = usersResponse.status === 'fulfilled' ? usersResponse.value?.users || [] : [];
        const groups = groupsResponse.status === 'fulfilled' ? groupsResponse.value?.groups || [] : [];

        if (!users?.length) console.error('No users found in the response.');
        if (!groups?.length) console.error('No groups found in the response.');

        return { users, groups };
      } catch (err) {
        const message = `Failed to fetch users and groups: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return undefined;
      }
    };

  const value = {
    users,
    groups,
    usersAndGroups: [...(groups || []), ...(users || [])],
    listUsers,
    listGroups,
    listGroupsAndUsers,
    CURRENT_USER_NAME,
    usersNameMap,
    groupsNameMap,
    usersAndGroupsNameMap,
    loading:
      usersQuery.isFetching ||
      usersQuery.isFetchingNextPage ||
      groupsQuery.isFetching ||
      groupsQuery.isFetchingNextPage,
  };

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
};

const useUsers = () => {
  const context = React.useContext(UsersContext);
  if (!context) {
    throw new Error('useUsers must be used within a UserProvider');
  }
  return context;
};

export { UsersProvider, useUsers };
